import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChartBarIcon,
  ClockIcon,
  UserGroupIcon,
  ExclamationTriangleIcon,
  FunnelIcon,
  EyeIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { 
  WorkflowState, 
  WorkflowPriority, 
  ProductWorkflowState, 
  WorkflowStatistics,
  WorkflowFilters,
  Product,
  User,
  WORKFLOW_STATES,
  WORKFLOW_PRIORITIES
} from '../../types/pim';
import WorkflowStatusBadge from './WorkflowStatusBadge';

interface WorkflowDashboardProps {
  statistics: WorkflowStatistics;
  products: Array<Product & { workflowState: ProductWorkflowState }>;
  availableReviewers: User[];
  onFilterChange: (filters: WorkflowFilters) => void;
  onViewProduct: (productId: number) => void;
  onRefresh: () => void;
  isLoading?: boolean;
  className?: string;
}

interface StatCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  color: string;
  subtitle?: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color, subtitle }) => (
  <div className={`bg-white rounded-lg border border-gray-200 p-6 ${color}`}>
    <div className="flex items-center">
      <div className="flex-shrink-0">
        {icon}
      </div>
      <div className="ml-5 w-0 flex-1">
        <dl>
          <dt className="text-sm font-medium text-gray-500 truncate">{title}</dt>
          <dd className="text-lg font-medium text-gray-900">{value.toLocaleString()}</dd>
          {subtitle && <dd className="text-sm text-gray-600">{subtitle}</dd>}
        </dl>
      </div>
    </div>
  </div>
);

interface ProductRowProps {
  product: Product & { workflowState: ProductWorkflowState };
  onView: (productId: number) => void;
}

const ProductRow: React.FC<ProductRowProps> = ({ product, onView }) => {
  const { t } = useTranslation();
  const isOverdue = product.workflowState.due_date &&
    new Date(product.workflowState.due_date) < new Date();

  return (
    <tr className={`hover:bg-gray-50 ${isOverdue ? 'bg-red-50' : ''}`}>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          <div>
            <div className="text-sm font-medium text-gray-900">{product.name}</div>
            <div className="text-sm text-gray-500">SKU: {product.sku}</div>
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <WorkflowStatusBadge state={product.workflowState.current_state} />
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
          product.workflowState.priority === 'urgent' ? 'bg-red-100 text-red-800' :
          product.workflowState.priority === 'high' ? 'bg-orange-100 text-orange-800' :
          product.workflowState.priority === 'medium' ? 'bg-blue-100 text-blue-800' :
          'bg-gray-100 text-gray-800'
        }`}>
          {WORKFLOW_PRIORITIES.find(p => p.value === product.workflowState.priority)?.label}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
        {product.workflowState.assignedReviewer 
          ? `${product.workflowState.assignedReviewer.first_name} ${product.workflowState.assignedReviewer.last_name}`
          : '-'
        }
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {product.workflowState.due_date ? (
          <div className={isOverdue ? 'text-red-600 font-medium' : ''}>
            {new Date(product.workflowState.due_date).toLocaleDateString()}
            {isOverdue && (
              <div className="flex items-center mt-1">
                <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                {t('pim.workflow.overdue')}
              </div>
            )}
          </div>
        ) : '-'}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {product.workflowState.submitted_at 
          ? new Date(product.workflowState.submitted_at).toLocaleDateString()
          : '-'
        }
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
        <button
          onClick={() => onView(product.id)}
          className="text-blue-600 hover:text-blue-900 flex items-center"
        >
          <EyeIcon className="h-4 w-4 mr-1" />
          {t('pim.workflow.viewDetails')}
        </button>
      </td>
    </tr>
  );
};

export const WorkflowDashboard: React.FC<WorkflowDashboardProps> = ({
  statistics,
  products,
  availableReviewers,
  onFilterChange,
  onViewProduct,
  onRefresh,
  isLoading = false,
  className = ''
}) => {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<WorkflowFilters>({
    page: 1,
    limit: 50
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    onFilterChange(filters);
  }, [filters, onFilterChange]);

  const handleFilterChange = (newFilters: Partial<WorkflowFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({ page: 1, limit: 50 });
  };

  const overdueCount = (products || []).filter(p => 
    p.workflowState?.due_date && new Date(p.workflowState.due_date) < new Date()
  ).length;

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('pim.workflow.title')}</h1>
          <p className="mt-1 text-sm text-gray-500">
            {t('pim.workflow.subtitle')}
          </p>
        </div>
        <button
          onClick={onRefresh}
          disabled={isLoading}
          className="flex items-center px-4 py-2 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
        >
          <ArrowPathIcon className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
          {t('common.refresh')}
        </button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statistics.stateDistribution.map(stat => {
          const stateConfig = WORKFLOW_STATES.find(s => s.value === stat.current_state);
          const iconColor = stat.current_state === 'draft' ? 'text-gray-500' :
                           stat.current_state === 'pending_review' ? 'text-yellow-500' :
                           stat.current_state === 'in_review' ? 'text-blue-500' :
                           stat.current_state === 'approved' ? 'text-green-500' :
                           stat.current_state === 'rejected' ? 'text-red-500' :
                           'text-purple-500';
          const bgColor = stat.current_state === 'draft' ? 'bg-gray-50' :
                         stat.current_state === 'pending_review' ? 'bg-yellow-50' :
                         stat.current_state === 'in_review' ? 'bg-blue-50' :
                         stat.current_state === 'approved' ? 'bg-green-50' :
                         stat.current_state === 'rejected' ? 'bg-red-50' :
                         'bg-purple-50';
          return (
            <StatCard
              key={stat.current_state}
              title={stateConfig?.label || stat.current_state}
              value={stat.count}
              icon={<ChartBarIcon className={`h-8 w-8 ${iconColor}`} />}
              color={bgColor}
            />
          );
        })}

        <StatCard
          title={t('pim.workflow.overdueItems')}
          value={overdueCount}
          icon={<ExclamationTriangleIcon className="h-8 w-8 text-red-500" />}
          color={overdueCount > 0 ? "bg-red-50 border-red-200" : "bg-gray-50"}
        />
      </div>

      {/* Priority Distribution */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 mb-6">{t('pim.workflow.priorityDistribution')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {statistics.priorityDistribution.map(stat => {
            const priorityConfig = WORKFLOW_PRIORITIES.find(p => p.value === stat.priority);
            const bgColor = stat.priority === 'urgent' ? 'bg-red-50' :
                           stat.priority === 'high' ? 'bg-orange-50' :
                           stat.priority === 'medium' ? 'bg-blue-50' :
                           'bg-gray-50';
            const borderColor = stat.priority === 'urgent' ? 'border-red-200' :
                               stat.priority === 'high' ? 'border-orange-200' :
                               stat.priority === 'medium' ? 'border-blue-200' :
                               'border-gray-200';
            return (
              <div key={stat.priority} className={`${bgColor} ${borderColor} border rounded-lg p-4 text-center`}>
                <div className={`text-3xl font-bold mb-2 ${
                  stat.priority === 'urgent' ? 'text-red-600' :
                  stat.priority === 'high' ? 'text-orange-600' :
                  stat.priority === 'medium' ? 'text-blue-600' :
                  'text-gray-600'
                }`}>
                  {stat.count}
                </div>
                <div className="text-sm font-medium text-gray-700">{priorityConfig?.label}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">{t('pim.workflow.workflowItems')}</h3>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900"
            >
              <FunnelIcon className="h-4 w-4 mr-1" />
              {t('pim.workflow.filters')}
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label htmlFor="state-filter" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('pim.workflow.status')}
                </label>
                <select
                  id="state-filter"
                  value={filters.state || ''}
                  onChange={(e) => handleFilterChange({ state: e.target.value as WorkflowState || undefined })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">{t('pim.workflow.allStatuses')}</option>
                  {WORKFLOW_STATES.map(state => (
                    <option key={state.value} value={state.value}>
                      {state.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="priority-filter" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('pim.workflow.priority')}
                </label>
                <select
                  id="priority-filter"
                  value={filters.priority || ''}
                  onChange={(e) => handleFilterChange({ priority: e.target.value as WorkflowPriority || undefined })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">{t('pim.workflow.allPriorities')}</option>
                  {WORKFLOW_PRIORITIES.map(priority => (
                    <option key={priority.value} value={priority.value}>
                      {priority.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="assignee-filter" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('pim.workflow.assignedTo')}
                </label>
                <select
                  id="assignee-filter"
                  value={filters.assignedTo || ''}
                  onChange={(e) => handleFilterChange({ assignedTo: Number(e.target.value) || undefined })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">{t('pim.workflow.allUsers')}</option>
                  {availableReviewers.map(reviewer => (
                    <option key={reviewer.id} value={reviewer.id}>
                      {reviewer.first_name} {reviewer.last_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-end">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={filters.overdue || false}
                    onChange={(e) => handleFilterChange({ overdue: e.target.checked || undefined })}
                    className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">{t('pim.workflow.overdueOnly')}</span>
                </label>
              </div>
            </div>

            <div className="mt-4 flex justify-end">
              <button
                onClick={clearFilters}
                className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
              >
                {t('common.clear')}
              </button>
            </div>
          </div>
        )}

        {/* Products Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('pim.workflow.table.product')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('pim.workflow.table.status')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('pim.workflow.table.priority')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('pim.workflow.table.assignedTo')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('pim.workflow.table.dueDate')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('pim.workflow.submitted')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('pim.workflow.table.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {products.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <div className="text-gray-500">
                      <UserGroupIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                      <p>{t('pim.workflow.noItemsDescription')}</p>
                    </div>
                  </td>
                </tr>
              ) : (
                products.map(product => (
                  <ProductRow
                    key={product.id}
                    product={product}
                    onView={onViewProduct}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Info */}
        {products.length > 0 && (
          <div className="px-6 py-3 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-700">
                {t('team.pagination.showing') || 'Showing'} {products.length} {t('pim.products.title').toLowerCase()}
              </p>
              {overdueCount > 0 && (
                <p className="text-sm text-red-600 font-medium">
                  {overdueCount} {t('pim.workflow.overdueItems').toLowerCase()}
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkflowDashboard;